import { useEffect, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/hooks/useLanguage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
const HomePage = lazy(() => import("./pages/HomePage"));

// Lazy-loaded routes for code splitting
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const ProjectsManager = lazy(() => import("./pages/admin/ProjectsManager"));
const ServicesManager = lazy(() => import("./pages/admin/ServicesManager"));
const LogosManager = lazy(() => import("./pages/admin/LogosManager"));
const TestimonialsManager = lazy(() => import("./pages/admin/TestimonialsManager"));
const BlogManager = lazy(() => import("./pages/admin/BlogManager"));
const HomepageManager = lazy(() => import("./pages/admin/HomepageManager"));
const SubmissionsManager = lazy(() => import("./pages/admin/SubmissionsManager"));
const TimelineManager = lazy(() => import("./pages/admin/TimelineManager"));
const MediaManager = lazy(() => import("./pages/admin/MediaManager"));
const SettingsManager = lazy(() => import("./pages/admin/SettingsManager"));
const AboutManager = lazy(() => import("./pages/admin/AboutManager"));
const SeoReportPage = lazy(() => import("./pages/admin/SeoReportPage"));
const PresentationsManager = lazy(() => import("./pages/admin/PresentationsManager"));
const PresentationView = lazy(() => import("./pages/PresentationView"));

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
  </div>
);

const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    <Navbar />
    <main>
      <Suspense fallback={<PageFallback />}>
        {children}
      </Suspense>
    </main>
    <Footer />
    <WhatsAppButton />
  </>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <ScrollToTop />
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<Suspense fallback={<PageFallback />}><AdminLogin /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<PageFallback />}><AdminLayout /></Suspense>}>
                <Route index element={<Suspense fallback={<PageFallback />}><DashboardPage /></Suspense>} />
                <Route path="projects" element={<Suspense fallback={<PageFallback />}><ProjectsManager /></Suspense>} />
                <Route path="services" element={<Suspense fallback={<PageFallback />}><ServicesManager /></Suspense>} />
                <Route path="logos" element={<Suspense fallback={<PageFallback />}><LogosManager /></Suspense>} />
                <Route path="testimonials" element={<Suspense fallback={<PageFallback />}><TestimonialsManager /></Suspense>} />
                <Route path="blog" element={<Suspense fallback={<PageFallback />}><BlogManager /></Suspense>} />
                <Route path="homepage" element={<Suspense fallback={<PageFallback />}><HomepageManager /></Suspense>} />
                <Route path="about" element={<Suspense fallback={<PageFallback />}><AboutManager /></Suspense>} />
                <Route path="submissions" element={<Suspense fallback={<PageFallback />}><SubmissionsManager /></Suspense>} />
                <Route path="timeline" element={<Suspense fallback={<PageFallback />}><TimelineManager /></Suspense>} />
                <Route path="media" element={<Suspense fallback={<PageFallback />}><MediaManager /></Suspense>} />
                <Route path="settings" element={<Suspense fallback={<PageFallback />}><SettingsManager /></Suspense>} />
                <Route path="seo-report" element={<Suspense fallback={<PageFallback />}><SeoReportPage /></Suspense>} />
                <Route path="presentations" element={<Suspense fallback={<PageFallback />}><PresentationsManager /></Suspense>} />
              </Route>

              {/* Private client presentations — link-gated, no navbar/footer,
                  never indexed and never linked from the public site. */}
              <Route path="/p/:token" element={<Suspense fallback={<PageFallback />}><PresentationView /></Suspense>} />

              {/* Public Routes: İngilizce kanonik adresler */}
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
              <Route path="/portfolio" element={<PublicLayout><PortfolioPage /></PublicLayout>} />
              <Route path="/portfolio/:id" element={<PublicLayout><ProjectDetailPage /></PublicLayout>} />
              <Route path="/news" element={<PublicLayout><NewsPage /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />

              {/* Türkçe kanonik adresler. Aynı bileşenler render edilir; dil
                  yol önekinden belirlenir (hooks/useLanguage.tsx). Eşleşme
                  listesi lib/localizedPaths.ts içindedir, prerender ve menü
                  linkleri de aynı listeyi kullanır. */}
              <Route path="/tr" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/tr/hakkimizda" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/tr/hizmetler" element={<PublicLayout><ServicesPage /></PublicLayout>} />
              <Route path="/tr/portfolyo" element={<PublicLayout><PortfolioPage /></PublicLayout>} />
              <Route path="/tr/haberler" element={<PublicLayout><NewsPage /></PublicLayout>} />
              <Route path="/tr/iletisim" element={<PublicLayout><ContactPage /></PublicLayout>} />
              <Route path="/tr/gizlilik" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
              <Route path="/en/services/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
              <Route path="/tr/hizmetler/:slug" element={<PublicLayout><ServiceDetailPage /></PublicLayout>} />
              <Route path="/en/blog/:slug" element={<PublicLayout><BlogArticlePage /></PublicLayout>} />
              <Route path="/tr/blog/:slug" element={<PublicLayout><BlogArticlePage /></PublicLayout>} />
              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
            </Routes>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
